import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type {
  TimeRecord,
  TimeRecordFilters,
  PaginatedTimeRecords,
  TimeRecordAggregateFilters,
  TimeRecordAggregateResponse,
  TimeRecordsStatistics,
} from '../types/time-records.types';

const TIME_RECORDS_QUERY_KEY = 'time-records';

/**
 * Hook to fetch paginated time records with filters
 */
export const useTimeRecords = (filters: TimeRecordFilters = {}) => {
  return useQuery({
    queryKey: [TIME_RECORDS_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedTimeRecords> => {
      const response = await apiClient.getTimeRecords(filters);
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      };
    },
    staleTime: 30 * 1000, // 30 seconds
    retry: 3,
  });
};

/**
 * Hook to fetch time records for a specific employee
 */
export const useTimeRecordsByEmployee = (
  employeeId: string,
  filters: Omit<TimeRecordFilters, 'employeeId'> = {}
) => {
  return useQuery({
    queryKey: [TIME_RECORDS_QUERY_KEY, 'by-employee', employeeId, filters],
    queryFn: async (): Promise<PaginatedTimeRecords> => {
      const response = await apiClient.getTimeRecordsByEmployee(employeeId, filters);
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      };
    },
    enabled: !!employeeId,
    staleTime: 30 * 1000,
    retry: 3,
  });
};

/**
 * Hook to fetch aggregated time records data
 */
export const useAggregateTimeRecords = (filters: TimeRecordAggregateFilters) => {
  return useQuery({
    queryKey: [TIME_RECORDS_QUERY_KEY, 'aggregate', filters],
    queryFn: async (): Promise<TimeRecordAggregateResponse> => {
      const response = await apiClient.aggregateTimeRecords(filters);
      return response;
    },
    enabled: !!filters.from && !!filters.to,
    staleTime: 60 * 1000, // 1 minute
    retry: 3,
  });
};

/**
 * Hook to fetch time records for the current authenticated user
 */
export const useMyTimeRecords = (filters: Omit<TimeRecordFilters, 'employeeId'> = {}) => {
  return useQuery({
    queryKey: [TIME_RECORDS_QUERY_KEY, 'my-records', filters],
    queryFn: async (): Promise<PaginatedTimeRecords> => {
      const response = await apiClient.getMyTimeRecords(filters);
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      };
    },
    staleTime: 30 * 1000,
    retry: 3,
  });
};

/**
 * Hook to fetch summary statistics for the current user
 */
export const useMySummary = (filters: { from?: string; to?: string } = {}) => {
  return useQuery({
    queryKey: [TIME_RECORDS_QUERY_KEY, 'my-summary', filters],
    queryFn: async () => {
      const response = await apiClient.getMySummary(filters);
      return response;
    },
    staleTime: 60 * 1000, // 1 minute
    retry: 3,
  });
};

/**
 * Hook to calculate statistics from time records data
 * Used for dashboard metrics
 */
export const useTimeRecordsStatistics = (
  data: TimeRecord[] | undefined
): TimeRecordsStatistics | null => {
  if (!data || data.length === 0) {
    return null;
  }

  const stats = data.reduce(
    (acc, record) => ({
      totalWorkedSeconds: acc.totalWorkedSeconds + record.worked_seconds,
      totalBreakSeconds: acc.totalBreakSeconds + record.break_seconds,
      totalOvertimeSeconds: acc.totalOvertimeSeconds + record.overtime_seconds,
      totalWorkingDays: acc.totalWorkingDays + 1,
      totalLateDays: acc.totalLateDays + (record.is_late ? 1 : 0),
      totalEarlyLeaveDays: acc.totalEarlyLeaveDays + (record.left_early ? 1 : 0),
      averageWorkDurationSeconds: 0, // Will be calculated after
      // Ночные смены
      totalNightMinutes: acc.totalNightMinutes + (record.night_minutes || 0),
      totalDayMinutes: acc.totalDayMinutes + (record.day_minutes || 0),
      totalNightShifts: acc.totalNightShifts + (record.is_night_shift ? 1 : 0),
    }),
    {
      totalWorkedSeconds: 0,
      totalBreakSeconds: 0,
      totalOvertimeSeconds: 0,
      totalWorkingDays: 0,
      totalLateDays: 0,
      totalEarlyLeaveDays: 0,
      averageWorkDurationSeconds: 0,
      // Ночные смены
      totalNightMinutes: 0,
      totalDayMinutes: 0,
      totalNightShifts: 0,
    }
  );

  // Calculate average
  if (stats.totalWorkingDays > 0) {
    stats.averageWorkDurationSeconds = Math.floor(
      stats.totalWorkedSeconds / stats.totalWorkingDays
    );
  }

  return stats;
};

/**
 * Hook to invalidate time records cache
 * Useful after creating/updating records
 */
export const useInvalidateTimeRecords = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: [TIME_RECORDS_QUERY_KEY] });
  };
};

/**
 * Hook to manually generate time records
 */
export interface GenerateTimeRecordsParams {
  start_date: string;
  end_date: string;
  employee_id?: string;
  employee_ids?: string[];
  overwrite?: boolean;
}

export interface GenerateTimeRecordsResult {
  generated_count: number;
  updated_count: number;
  employees_processed: number;
  errors?: string[];
}

export const useGenerateTimeRecords = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: GenerateTimeRecordsParams): Promise<GenerateTimeRecordsResult> => {
      return apiClient.generateTimeRecords(params);
    },
    onSuccess: () => {
      // Invalidate time records cache to refetch data
      queryClient.invalidateQueries({ queryKey: [TIME_RECORDS_QUERY_KEY] });
    },
  });
};
